// La espera de una generación DENTRO de una card del chat, con la MISMA animación que
// la columna de resultados del modo Modelo (`RecuadroGenerando`: el recuadro azul con la
// foto de fondo, el núcleo de carga y la barra sembrada desde el inicio) —
// ADR-2026-09-09-las-cards-del-chat-generan-como-el-panel y
// ADR-2026-09-09-video-y-audio-generan-como-el-panel.
//
// Antes la card mostraba una Lottie genérica que no tenía nada que ver con lo que el
// usuario ve en el resto de la app cuando genera. Ahora ve un recuadro por imagen
// esperada, con la proporción del formato elegido; el video, el mismo recuadro con su
// pacing; y el audio, una versión baja con el mismo fondo y el mismo ícono, y un
// ecualizador donde el recuadro de imagen tiene la foto.

import React, { useRef } from 'react';
import { FondoCarga } from '../../components/loading/NucleoCarga';
import { SparklesLoaderInline } from '../../components/loading/SparklesLoader';
import { RecuadroGenerando } from './components/PanelTandaCreaciones';
import { ratioDelFormatoElegido } from './modes/promptLibre/promptLibre';
import {
  ANCHO_MAX_RECUADRO_UNICO_PX,
  barrasDelEcualizador,
  columnasParaRecuadros,
  recuadrosADibujar,
} from './recuadrosEnCard';

const GRILLA: Record<1 | 2 | 3, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
};

export const GenerandoEnCard: React.FC<{
  /** Cuántas imágenes se esperan (un recuadro por cada una, hasta 6). */
  cantidad?: number;
  /** Slug del formato (post / story / …): da la proporción de los recuadros. */
  format?: string | null;
  /** Cuándo arrancó la generación: siembra la barra. Sin esto, desde el montaje. */
  creadaEn?: number;
  esVideo?: boolean;
  /** Pacing de la barra (ms). Default el de imagen; el video pasa el suyo. */
  duracionMs?: number;
  /** Línea de abajo. `null` la oculta. */
  subtexto?: string | null;
}> = ({
  cantidad = 1,
  format,
  creadaEn,
  esVideo = false,
  duracionMs,
  subtexto = 'Esto puede tardar unos segundos. Podés seguir usando el chat.',
}) => {
  // Si la card no guardó cuándo arrancó, la barra arranca al montar; y no se reinicia
  // en cada render.
  const montadoEn = useRef(Date.now());
  const inicio = creadaEn ?? montadoEn.current;
  const n = recuadrosADibujar(cantidad);
  const columnas = columnasParaRecuadros(n);
  const ratio = ratioDelFormatoElegido(format);
  return (
    <div className="px-5 py-5 flex flex-col items-center gap-3">
      <div
        className={`grid w-full gap-2 ${GRILLA[columnas]}`}
        style={columnas === 1 ? { maxWidth: ANCHO_MAX_RECUADRO_UNICO_PX } : undefined}
      >
        {Array.from({ length: n }, (_, i) => (
          <RecuadroGenerando
            key={i}
            ratio={ratio}
            creadaEn={inicio}
            esVideo={esVideo}
            duracionMs={duracionMs}
          />
        ))}
      </div>
      {subtexto ? (
        <p className="text-[13px] text-[#3C3C43]/55 text-center">{subtexto}</p>
      ) : null}
    </div>
  );
};

/** Cuántas barras tiene el ecualizador. A 3px + 3px de gap ocupan ~170px. */
const BARRAS = 28;

/**
 * La espera de un AUDIO: el mismo fondo y el mismo ícono que el recuadro de imagen,
 * pero bajo y a lo ancho (un audio no tiene proporción), con el ícono y el título a un
 * costado y un ecualizador ocupando el resto. Las barras se animan con `atlas-eq`
 * (en `index.css`, como `atlas-indet`: un solo bloque de CSS para N recuadros) y son
 * deterministas (`barrasDelEcualizador`), así el dibujo no salta en cada render.
 */
export const GenerandoAudioEnCard: React.FC<{
  titulo?: string;
  subtexto?: string | null;
}> = ({ titulo = 'Generando el audio…', subtexto = null }) => {
  const barras = barrasDelEcualizador(BARRAS);
  return (
    <div className="px-5 py-5 flex flex-col items-center gap-3">
      <div className="relative h-24 w-full overflow-hidden rounded-2xl bg-brand-primary-apagado text-white">
        <FondoCarga />
        <div className="absolute inset-0 z-10 flex items-center gap-4 px-5">
          <div className="flex shrink-0 items-center gap-3">
            <SparklesLoaderInline className="size-10 text-white" />
            <p className="text-[13px] font-[600] leading-[18px] text-[#FAFAFA]">{titulo}</p>
          </div>
          <div
            className="flex h-12 min-w-0 flex-1 items-center justify-end gap-[3px]"
            aria-hidden="true"
          >
            {barras.map((b, i) => (
              <span
                key={i}
                className="atlas-eq-bar block w-[3px] rounded-full bg-white/90"
                style={{
                  height: `${b.alto}%`,
                  animation: `atlas-eq ${b.duracionMs}ms ease-in-out infinite`,
                  animationDelay: `${b.desfaseMs}ms`,
                }}
              />
            ))}
          </div>
        </div>
      </div>
      {subtexto ? (
        <p className="text-[13px] text-[#3C3C43]/55 text-center">{subtexto}</p>
      ) : null}
    </div>
  );
};
